import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import type {
  CreateBatchRoomRatesParams,
  CreateBatchRoomRatesResponse,
} from '~/hooks/apis/reservation/useCreateBatchRoomRates';

import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { AppliedFlashRoomRate } from '../types';

export interface ApplyRoomRatesParams {
  createBatchRoomRates: UseMutateAsyncFunction<CreateBatchRoomRatesResponse, unknown, CreateBatchRoomRatesParams>;
  createdRatePlans: Array<App.RatePlan>;
  propertyId: string | undefined;
  roomRateRoomsByRoomTypeId: Map<string, string>;
  selectedRoomRatePlanIds: Record<string, Array<string>>;
}

export interface ApplyRoomRatesResult {
  createdRoomRates: Array<AppliedFlashRoomRate>;
  errorMessage?: string;
  /**
   * (roomType, ratePlan) pairs that already had a room rate when freshly checked right before
   * writing -- not created this run, so never rolled back or re-capacitied, but still real and
   * usable for package linking.
   */
  reusedRoomRates: Array<AppliedFlashRoomRate>;
  success: boolean;
}

/**
 * Batch-creates room rates for every selected (roomType, ratePlan) pair, one roomType at a time,
 * stopping at the first failure. Called exactly once per Submission attempt, and any failure
 * rolls back everything created so far.
 *
 * Each roomType's current room rates are fetched fresh immediately before writing and any pair
 * that already exists is skipped rather than recreated: svc-reservation has no uniqueness
 * constraint on (room_type_id, rate_plan_id), so nothing on the backend stops a genuine duplicate
 * if the CM's cached Room Rates selection is stale by the time Submission runs (another attempt
 * landed since, a retry replayed a not-fully-reset draft, etc.).
 */
export async function applyRoomRates({
  createBatchRoomRates,
  createdRatePlans,
  propertyId,
  roomRateRoomsByRoomTypeId,
  selectedRoomRatePlanIds,
}: ApplyRoomRatesParams): Promise<ApplyRoomRatesResult> {
  const createdRoomRates: Array<AppliedFlashRoomRate> = [];
  const reusedRoomRates: Array<AppliedFlashRoomRate> = [];

  if (createdRatePlans.length === 0) {
    return {
      createdRoomRates,
      errorMessage: 'Create at least one Flash rate plan before applying room rates.',
      reusedRoomRates,
      success: false,
    };
  }

  const selectedRoomRateAssignments = Object.entries(selectedRoomRatePlanIds).filter(
    ([, ratePlanIds]) => ratePlanIds.length > 0,
  );

  if (selectedRoomRateAssignments.length === 0) {
    return { createdRoomRates, errorMessage: 'Select at least one room.', reusedRoomRates, success: false };
  }

  if (!propertyId) {
    return {
      createdRoomRates,
      errorMessage: 'Select or create a property before applying room rates.',
      reusedRoomRates,
      success: false,
    };
  }

  const roomRateAssignments = selectedRoomRateAssignments.flatMap(([roomTypeId, ratePlanIds]) =>
    ratePlanIds
      .map((ratePlanId) => {
        const ratePlan = createdRatePlans.find((plan) => plan.id === ratePlanId);

        return ratePlan?.id ? { roomTypeId, ratePlan } : null;
      })
      .filter((assignment): assignment is { roomTypeId: string; ratePlan: App.RatePlan } => !!assignment),
  );

  const assignmentsByRoomTypeId = new Map<string, Array<App.RatePlan>>();

  roomRateAssignments.forEach(({ roomTypeId, ratePlan }) => {
    const ratePlans = assignmentsByRoomTypeId.get(roomTypeId) ?? [];
    assignmentsByRoomTypeId.set(roomTypeId, ratePlans.concat(ratePlan));
  });

  const pendingAssignmentsByRoomTypeId = new Map<string, Array<App.RatePlan>>();

  // One independent fetch per room type -- run them concurrently rather than one at a time, or a
  // property with many rooms turns this pre-flight into its own N+1 delay before any create call.
  const existingRoomRatesByRoomTypeId = await Promise.all(
    Array.from(assignmentsByRoomTypeId.keys()).map(
      async (roomTypeId): Promise<readonly [string, Array<App.RoomRate>]> => {
        try {
          const response = await ReservationService.getRoomRates(propertyId, roomTypeId);
          const result: unknown = response?.result;
          return [roomTypeId, Array.isArray(result) ? (result as Array<App.RoomRate>) : []];
        } catch (error) {
          // Best-effort, same as the rate-plan-name pre-flight: a failed lookup falls through
          // to let the create call report it.
          reportError(error);
          return [roomTypeId, []];
        }
      },
    ),
  );

  existingRoomRatesByRoomTypeId.forEach(([roomTypeId, existingRoomRates]) => {
    const ratePlans = assignmentsByRoomTypeId.get(roomTypeId) ?? [];
    const existingRoomRateIdByRatePlanId = new Map(
      existingRoomRates.flatMap((roomRate) =>
        roomRate?.id && roomRate.rate_plan?.id ? [[roomRate.rate_plan.id, roomRate.id] as const] : [],
      ),
    );
    const pendingRatePlans: Array<App.RatePlan> = [];

    ratePlans.forEach((ratePlan) => {
      const existingRoomRateId = existingRoomRateIdByRatePlanId.get(ratePlan.id);

      if (existingRoomRateId) {
        reusedRoomRates.push({
          ratePlanId: ratePlan.id,
          ratePlanName: ratePlan.name,
          roomName: roomRateRoomsByRoomTypeId.get(roomTypeId) || roomTypeId,
          roomRateId: existingRoomRateId,
          roomTypeId,
        });
      } else {
        pendingRatePlans.push(ratePlan);
      }
    });

    if (pendingRatePlans.length > 0) {
      pendingAssignmentsByRoomTypeId.set(roomTypeId, pendingRatePlans);
    }
  });

  const pendingAssignmentCount = Array.from(pendingAssignmentsByRoomTypeId.values()).reduce(
    (total, ratePlans) => total + ratePlans.length,
    0,
  );

  try {
    for (const [roomTypeId, ratePlans] of pendingAssignmentsByRoomTypeId) {
      const ratePlanIds = ratePlans.map((ratePlan) => ratePlan.id);
      const response = await createBatchRoomRates({
        propertyId,
        ratePlanIds,
        roomTypeId,
      });
      const allResults = response?.result;
      const ratePlanIdSet = new Set(ratePlanIds);
      const roomRateByRatePlanId = new Map<string, App.RoomRate>();

      if (Array.isArray(allResults)) {
        allResults.forEach((roomRate) => {
          const ratePlanId = roomRate?.rate_plan?.id;

          if (roomRate?.id && ratePlanId && ratePlanIdSet.has(ratePlanId) && !roomRateByRatePlanId.has(ratePlanId)) {
            roomRateByRatePlanId.set(ratePlanId, roomRate);
          }
        });
      }

      const results = ratePlanIds
        .map((ratePlanId) => roomRateByRatePlanId.get(ratePlanId))
        .filter((roomRate): roomRate is App.RoomRate => !!roomRate);

      if (!Array.isArray(allResults) || results.length !== ratePlanIds.length) {
        throw new Error('Unexpected response from createBatchRoomRates');
      }

      results.forEach((roomRate) => {
        if (!roomRate?.id) {
          throw new Error('Unexpected response from createBatchRoomRates');
        }

        const ratePlanId = roomRate.rate_plan?.id;
        const ratePlan = ratePlans.find((plan) => plan.id === ratePlanId);

        if (!ratePlanId || !ratePlan) {
          throw new Error('Unexpected response from createBatchRoomRates');
        }

        createdRoomRates.push({
          ratePlanId,
          ratePlanName: ratePlan.name,
          roomName: roomRateRoomsByRoomTypeId.get(roomTypeId) || roomTypeId,
          roomRateId: roomRate.id,
          roomTypeId,
        });
      });
    }

    return { createdRoomRates, reusedRoomRates, success: true };
  } catch (error) {
    reportError(error);
    const createdCount = createdRoomRates.length;

    return {
      createdRoomRates,
      errorMessage:
        createdCount > 0
          ? `Applied ${createdCount} of ${pendingAssignmentCount} room rate assignments. The whole attempt is rolled back regardless -- fix the issue and try again. ${getErrorMessage(error)}`
          : `Unable to apply rate plans. ${getErrorMessage(error)}`,
      reusedRoomRates,
      success: false,
    };
  }
}
